/**
 * Streaming platform affiliate links for a song.
 * Generates search-based URLs to encourage official streaming.
 */
import { ExternalLink } from "lucide-react";

interface StreamingLinksProps {
  songName: string;
  artistName: string;
  /** Compact mode for card overlays */
  compact?: boolean;
  className?: string;
}

const PLATFORMS = [
  {
    name: "Apple Music",
    icon: () => (
      <svg viewBox="0 0 24 24" className="h-full w-full" fill="currentColor">
        <path d="M23.994 6.124a9.23 9.23 0 0 0-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 0 0-1.877-.726 10.496 10.496 0 0 0-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.802.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03c.525 0 1.048-.034 1.57-.1.823-.106 1.597-.35 2.296-.81a5.046 5.046 0 0 0 1.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.404-2.15-1.252-.376-.873-.137-1.96.73-2.533.363-.24.78-.378 1.207-.472.596-.13 1.2-.218 1.795-.345.36-.078.533-.29.56-.66.003-.038.006-.075.006-.112v-4.557c0-.076-.01-.152-.03-.226-.06-.22-.214-.336-.44-.305-.325.044-.648.114-.968.19l-3.988.9c-.027.007-.055.013-.083.023-.322.1-.447.26-.467.6v.07l-.004 6.705c0 .446-.053.885-.26 1.29-.3.586-.77.957-1.4 1.124-.36.096-.726.15-1.095.16-.922.026-1.726-.39-2.084-1.22-.376-.87-.152-1.94.707-2.53.37-.252.795-.39 1.233-.49.535-.122 1.075-.213 1.61-.333.445-.1.63-.33.656-.784v-.12l.002-8.335c0-.2.03-.394.098-.582.1-.282.31-.46.594-.528.086-.02.173-.04.26-.054l5.602-1.26c.2-.045.403-.09.606-.118.432-.058.674.17.676.61l.003.56z" />
      </svg>
    ),
    getUrl: (song: string, artist: string) =>
      `https://music.apple.com/search?term=${encodeURIComponent(`${artist} ${song}`)}`,
    color: "hover:text-[#FA243C]",
  },
  {
    name: "Spotify",
    icon: () => (
      <svg viewBox="0 0 24 24" className="h-full w-full" fill="currentColor">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
      </svg>
    ),
    getUrl: (song: string, artist: string) =>
      `https://open.spotify.com/search/${encodeURIComponent(`${artist} ${song}`)}`,
    color: "hover:text-[#1DB954]",
  },
  {
    name: "YouTube Music",
    icon: () => (
      <svg viewBox="0 0 24 24" className="h-full w-full" fill="currentColor">
        <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L15.816 12l-6.132 3.54z" />
      </svg>
    ),
    getUrl: (song: string, artist: string) =>
      `https://music.youtube.com/search?q=${encodeURIComponent(`${artist} ${song}`)}`,
    color: "hover:text-[#FF0000]",
  },
  {
    name: "Amazon Music",
    icon: () => (
      <svg viewBox="0 0 24 24" className="h-full w-full" fill="currentColor">
        <path d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.44-2.186 1.44-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.7-3.182v.683zm3.186 7.705a.66.66 0 0 1-.753.069c-1.06-.881-1.25-1.289-1.834-2.128-1.756 1.789-2.998 2.325-5.268 2.325-2.689 0-4.786-1.66-4.786-4.984 0-2.596 1.407-4.363 3.411-5.227 1.735-.756 4.16-.891 6.014-1.1v-.41c0-.753.058-1.642-.383-2.294-.385-.579-1.124-.819-1.775-.819-1.205 0-2.277.618-2.54 1.897a.456.456 0 0 1-.392.399l-2.465-.266a.39.39 0 0 1-.33-.462C6.594 1.837 9.655.5 12.41.5c1.407 0 3.245.374 4.353 1.439 1.407 1.318 1.272 3.076 1.272 4.991v4.522c0 1.36.564 1.955 1.095 2.69a.47.47 0 0 1-.017.663c-.72.6-2.001 1.714-2.706 2.338l-.003-.005-.26.255zM21.78 19.7c-1.593 1.18-3.907 1.8-5.893 1.8-2.788 0-5.298-1.031-7.197-2.748a.26.26 0 0 1-.027-.344c.284-.316.596-.452.87-.452.262 0 .507.15.757.344 1.613 1.344 3.614 2.148 5.693 2.148 1.663 0 3.495-.688 4.834-1.413.286-.155.544.172.348.412l-.003.003-.001-.001-.022.028-.033.04c-.064.078-.142.145-.226.183h-.1z" />
      </svg>
    ),
    getUrl: (song: string, artist: string) =>
      `https://music.amazon.com/search/${encodeURIComponent(`${artist} ${song}`)}`,
    color: "hover:text-[#25D1DA]",
  },
];

const StreamingLinks = ({ songName, artistName, compact = false, className = "" }: StreamingLinksProps) => {
  if (!songName || !artistName) return null;

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {PLATFORMS.map((p) => (
          <a
            key={p.name}
            href={p.getUrl(songName, artistName)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Listen on ${p.name}`}
            className={`flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:bg-white/20 hover:scale-110 ${p.color}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-3 w-3">
              <p.icon />
            </div>
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <ExternalLink className="h-3 w-3" />
        <span className="font-display font-medium">Listen officially</span>
      </div>
      <div className="flex items-center gap-1.5">
        {PLATFORMS.map((p) => (
          <a
            key={p.name}
            href={p.getUrl(songName, artistName)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Listen on ${p.name}`}
            className={`flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/80 text-muted-foreground transition-all hover:bg-secondary hover:scale-110 ${p.color}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-4 w-4">
              <p.icon />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default StreamingLinks;
